/**
 * TicketPerformancePrototype — frontend-only manager dashboard for evaluating
 * how quickly each assignee closes anomaly tickets.
 *
 * KPIs (top strip):
 *   • Total Open · Total Resolved (last 30d) · Avg Resolution · SLA Breaches
 *
 * Leaderboard (sortable):
 *   • Assignee · Open · Resolved 30d · Avg Resolution · Median · SLA % (≤48h target)
 *   · Oldest Open Age · Velocity sparkline · Performance band
 *
 * Click a row → drawer with their open + recent resolved tickets and per-ticket
 * resolution time bar.
 *
 * Wire-up plan (when promoted from prototype): aggregate the existing tickets
 * collection grouped by assigned_to: count open, count resolved within window,
 * compute (resolved_at - created_at) percentiles, and the SLA target as a config.
 */
import { useState, useMemo } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import {
  Trophy, Clock, AlertTriangle, CheckCircle2, TrendingUp, Search, Filter,
  Award, Activity, ArrowUp, ArrowDown, Sparkles, Timer, Target, Flame,
  ChevronRight, User, Ticket as TicketIcon
} from 'lucide-react';

// ─── Dummy seed data ─────────────────────────────────────────────────────
// Realistic teammates pulled from the live /api/users responses we've seen
const SEED_ASSIGNEES = [
  {
    id: 'u_pankaj', name: 'Pankaj Sharma', role: 'manager', avatar: 'PS',
    open: 4, resolved_30d: 28,
    avg_hours: 14.2, median_hours: 9.5, p90_hours: 31.0,
    sla_pct: 92, oldest_open_hours: 18,
    trend: [3, 5, 4, 6, 7, 5, 4, 8, 6, 9, 7, 5, 6, 8],
  },
  {
    id: 'u_anupam', name: 'Anupam Verma', role: 'manager', avatar: 'AV',
    open: 7, resolved_30d: 22,
    avg_hours: 21.8, median_hours: 18.0, p90_hours: 47.5,
    sla_pct: 78, oldest_open_hours: 52,
    trend: [4, 3, 5, 4, 6, 5, 3, 4, 6, 7, 8, 6, 5, 7],
  },
  {
    id: 'u_ritu', name: 'Ritu Singh', role: 'restricted_user', avatar: 'RS',
    open: 2, resolved_30d: 35,
    avg_hours: 8.6, median_hours: 6.0, p90_hours: 18.0,
    sla_pct: 97, oldest_open_hours: 6,
    trend: [6, 8, 7, 9, 8, 10, 7, 9, 11, 8, 9, 10, 12, 9],
  },
  {
    id: 'u_pooja', name: 'Pooja Verma', role: 'restricted_user', avatar: 'PV',
    open: 11, resolved_30d: 14,
    avg_hours: 38.4, median_hours: 32.0, p90_hours: 89.0,
    sla_pct: 54, oldest_open_hours: 124,
    trend: [2, 3, 1, 2, 4, 3, 2, 1, 3, 4, 2, 3, 5, 2],
  },
  {
    id: 'u_admin', name: 'System Administrator', role: 'admin', avatar: 'SA',
    open: 3, resolved_30d: 19,
    avg_hours: 16.1, median_hours: 11.0, p90_hours: 38.0,
    sla_pct: 84, oldest_open_hours: 22,
    trend: [4, 5, 4, 6, 3, 5, 6, 7, 4, 5, 6, 5, 7, 5],
  },
  {
    id: 'u_divya', name: 'Divya Patel', role: 'manager', avatar: 'DP',
    open: 5, resolved_30d: 24,
    avg_hours: 19.5, median_hours: 14.0, p90_hours: 41.0,
    sla_pct: 81, oldest_open_hours: 36,
    trend: [3, 4, 5, 3, 4, 6, 5, 4, 6, 5, 7, 6, 5, 6],
  },
  {
    id: 'u_aanchal', name: 'Aanchal Sharma', role: 'restricted_user', avatar: 'AS',
    open: 1, resolved_30d: 31,
    avg_hours: 11.3, median_hours: 8.5, p90_hours: 24.0,
    sla_pct: 94, oldest_open_hours: 4,
    trend: [5, 7, 6, 8, 7, 9, 6, 8, 10, 7, 8, 9, 11, 8],
  },
];

// SLA target (hours) — would be admin-configurable in production
const SLA_TARGET = 48;

// ─── Helper utilities ────────────────────────────────────────────────────
function fmtHours(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function performanceBand(a) {
  // Score: SLA%, avg time vs target, open backlog
  const slaScore = a.sla_pct;
  const speedScore = Math.max(0, 100 - (a.avg_hours / SLA_TARGET) * 50);
  const backlogScore = Math.max(0, 100 - a.open * 8);
  const total = slaScore * 0.5 + speedScore * 0.3 + backlogScore * 0.2;
  if (total >= 85) return { label: 'Excellent', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' };
  if (total >= 70) return { label: 'On Track',  color: 'bg-blue-50 text-blue-700 border-blue-200',         dot: 'bg-blue-500' };
  if (total >= 55) return { label: 'Watch',     color: 'bg-amber-50 text-amber-700 border-amber-200',     dot: 'bg-amber-500' };
  return                   { label: 'At Risk',  color: 'bg-rose-50 text-rose-700 border-rose-200',         dot: 'bg-rose-500' };
}

function Sparkline({ data, color = '#7c3aed' }) {
  const max = Math.max(...data, 1);
  const w = 80, h = 22;
  const stepX = w / (data.length - 1);
  const points = data.map((v, i) => `${i * stepX},${h - (v / max) * h}`).join(' ');
  return (
    <svg width={w} height={h} className="inline-block align-middle">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      <polyline
        fill={color} fillOpacity="0.08" stroke="none"
        points={`0,${h} ${points} ${w},${h}`}
      />
    </svg>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────
export default function TicketPerformancePrototype() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('sla_pct');
  const [sortDir, setSortDir] = useState('desc');
  const [roleFilter, setRoleFilter] = useState('all');
  const [period, setPeriod] = useState('30d');
  const [drawerUser, setDrawerUser] = useState(null);

  // Aggregate KPIs
  const kpi = useMemo(() => {
    const total_open = SEED_ASSIGNEES.reduce((s, a) => s + a.open, 0);
    const total_resolved = SEED_ASSIGNEES.reduce((s, a) => s + a.resolved_30d, 0);
    const weighted_avg = SEED_ASSIGNEES.reduce((s, a) => s + a.avg_hours * a.resolved_30d, 0) / total_resolved;
    const sla_breaches = SEED_ASSIGNEES.reduce((s, a) => s + Math.round(a.resolved_30d * (1 - a.sla_pct / 100)), 0);
    const top = [...SEED_ASSIGNEES].sort((a, b) => b.sla_pct - a.sla_pct)[0];
    const bottom = [...SEED_ASSIGNEES].sort((a, b) => a.sla_pct - b.sla_pct)[0];
    return { total_open, total_resolved, weighted_avg, sla_breaches, top, bottom };
  }, []);

  const rows = useMemo(() => {
    let r = SEED_ASSIGNEES.filter(a =>
      (!search || a.name.toLowerCase().includes(search.toLowerCase())) &&
      (roleFilter === 'all' || a.role === roleFilter)
    );
    r.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return r;
  }, [search, sortBy, sortDir, roleFilter]);

  const SortHeader = ({ field, children, align = 'left' }) => (
    <button
      onClick={() => {
        if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortBy(field); setSortDir('desc'); }
      }}
      className={`inline-flex items-center gap-0.5 hover:text-violet-700 transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`}
    >
      {children}
      {sortBy === field && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  );

  return (
    <div className="space-y-5 p-6" data-testid="ticket-performance-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            Ticket Performance
            <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-violet-100 text-violet-700 rounded font-semibold">Prototype</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">How quickly is each teammate closing anomaly tickets? SLA target: ≤ {SLA_TARGET}h</p>
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-md overflow-hidden bg-white">
          {[
            { v: '7d',  l: '7 days' },
            { v: '30d', l: '30 days' },
            { v: '90d', l: '90 days' },
            { v: 'all', l: 'All time' },
          ].map(p => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
              className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${period === p.v ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 border-l-4 border-l-amber-500">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> Open</p>
          <p className="text-2xl font-bold text-amber-700 mt-0.5">{kpi.total_open}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">across {SEED_ASSIGNEES.length} assignees</p>
        </Card>
        <Card className="p-3 border-l-4 border-l-emerald-500">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><CheckCircle2 className="w-2.5 h-2.5" /> Resolved · {period}</p>
          <p className="text-2xl font-bold text-emerald-700 mt-0.5">{kpi.total_resolved}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">+12% vs prior period</p>
        </Card>
        <Card className="p-3 border-l-4 border-l-violet-500">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Timer className="w-2.5 h-2.5" /> Avg Resolution</p>
          <p className="text-2xl font-bold text-violet-700 mt-0.5">{fmtHours(kpi.weighted_avg)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">team-wide weighted</p>
        </Card>
        <Card className="p-3 border-l-4 border-l-rose-500">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> SLA Breaches</p>
          <p className="text-2xl font-bold text-rose-700 mt-0.5">{kpi.sla_breaches}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">tickets &gt; {SLA_TARGET}h to close</p>
        </Card>
        <Card className="p-3 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/40 to-white">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Award className="w-2.5 h-2.5 text-amber-500" /> Top Performer</p>
          <p className="text-sm font-bold text-emerald-700 mt-0.5">{kpi.top.name}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{kpi.top.sla_pct}% SLA · {fmtHours(kpi.top.avg_hours)} avg</p>
        </Card>
      </div>

      {/* Highlight banner: at-risk performer */}
      {kpi.bottom.sla_pct < 70 && (
        <Card className="p-3 border-l-4 border-l-rose-500 bg-rose-50/30 flex items-center gap-3" data-testid="at-risk-banner">
          <div className="w-9 h-9 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
            <Flame className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-900">⚠ Needs attention: {kpi.bottom.name}</p>
            <p className="text-xs text-rose-700">
              {kpi.bottom.sla_pct}% SLA compliance · {fmtHours(kpi.bottom.avg_hours)} avg · {kpi.bottom.open} open tickets ·
              oldest is <strong>{fmtHours(kpi.bottom.oldest_open_hours)} old</strong>
            </p>
          </div>
          <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-100" onClick={() => setDrawerUser(kpi.bottom)}>
            Review tickets <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search assignee..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 text-xs"
            data-testid="perf-search"
          />
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-md overflow-hidden bg-white">
          {[
            { v: 'all',             l: 'All Roles' },
            { v: 'admin',           l: 'Admin' },
            { v: 'manager',         l: 'Managers' },
            { v: 'restricted_user', l: 'Sales' },
          ].map(r => (
            <button key={r.v} onClick={() => setRoleFilter(r.v)}
              className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${roleFilter === r.v ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {r.l}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-slate-400 ml-auto"><Filter className="w-3 h-3 inline mr-1" />{rows.length} of {SEED_ASSIGNEES.length} assignees</span>
      </div>

      {/* Leaderboard */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/40 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-violet-500" /> Assignee Leaderboard
          </p>
          <span className="text-[9px] text-slate-400 italic">Sorted by {sortBy.replace('_', ' ')} ({sortDir})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/60 border-b border-slate-100">
              <tr className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                <th className="text-left px-4 py-2.5">#</th>
                <th className="text-left px-4 py-2.5">Assignee</th>
                <th className="text-right px-3 py-2.5"><SortHeader field="open" align="right">Open</SortHeader></th>
                <th className="text-right px-3 py-2.5"><SortHeader field="resolved_30d" align="right">Resolved</SortHeader></th>
                <th className="text-right px-3 py-2.5"><SortHeader field="avg_hours" align="right">Avg Time</SortHeader></th>
                <th className="text-right px-3 py-2.5"><SortHeader field="median_hours" align="right">Median</SortHeader></th>
                <th className="text-right px-3 py-2.5"><SortHeader field="sla_pct" align="right">SLA %</SortHeader></th>
                <th className="text-right px-3 py-2.5"><SortHeader field="oldest_open_hours" align="right">Oldest Open</SortHeader></th>
                <th className="text-center px-3 py-2.5">14-day Trend</th>
                <th className="text-center px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => {
                const band = performanceBand(a);
                const slaColor = a.sla_pct >= 90 ? 'text-emerald-600' : a.sla_pct >= 75 ? 'text-blue-600' : a.sla_pct >= 60 ? 'text-amber-600' : 'text-rose-600';
                const oldestColor = a.oldest_open_hours <= 24 ? 'text-emerald-600' : a.oldest_open_hours <= 72 ? 'text-amber-600' : 'text-rose-600';
                return (
                  <tr
                    key={a.id}
                    onClick={() => setDrawerUser(a)}
                    className="border-b border-slate-100 hover:bg-violet-50/40 cursor-pointer transition-colors"
                    data-testid={`perf-row-${a.id}`}
                  >
                    <td className="px-4 py-3 text-slate-400 font-medium">
                      {i === 0 && <Trophy className="w-3.5 h-3.5 text-amber-500 inline" />} {i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold">{a.avatar}</div>
                        <div>
                          <p className="font-medium text-slate-800">{a.name}</p>
                          <p className="text-[9px] text-slate-400 capitalize">{a.role.replace('_', ' ')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-amber-700">{a.open}</td>
                    <td className="px-3 py-3 text-right font-medium text-emerald-700">{a.resolved_30d}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{fmtHours(a.avg_hours)}</td>
                    <td className="px-3 py-3 text-right text-slate-500">{fmtHours(a.median_hours)}</td>
                    <td className={`px-3 py-3 text-right font-bold ${slaColor}`}>{a.sla_pct}%</td>
                    <td className={`px-3 py-3 text-right ${oldestColor} ${a.oldest_open_hours > 72 ? 'font-bold' : ''}`}>{fmtHours(a.oldest_open_hours)}</td>
                    <td className="px-3 py-3 text-center"><Sparkline data={a.trend} color={a.sla_pct >= 80 ? '#10b981' : '#f59e0b'} /></td>
                    <td className="px-3 py-3 text-center">
                      <Badge variant="outline" className={`text-[9px] ${band.color}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${band.dot}`} />{band.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Footnote on metric definitions */}
      <Card className="p-3 bg-slate-50/40 border-dashed">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-violet-500" /> How metrics are calculated
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] text-slate-600">
          <div><strong className="text-slate-700">Open</strong>: tickets currently assigned & not resolved.</div>
          <div><strong className="text-slate-700">Resolved · 30d</strong>: tickets closed in selected period.</div>
          <div><strong className="text-slate-700">Avg / Median</strong>: time from <em>created_at</em> to <em>resolved_at</em>.</div>
          <div><strong className="text-slate-700">SLA %</strong>: % of resolved tickets closed within {SLA_TARGET}h target.</div>
          <div><strong className="text-slate-700">Oldest Open</strong>: max age of any unresolved ticket.</div>
          <div><strong className="text-slate-700">Trend</strong>: daily resolved-ticket count, last 14 days.</div>
          <div><strong className="text-slate-700">Status band</strong>: weighted score of SLA · Speed · Backlog.</div>
          <div><strong className="text-slate-700">Top performer</strong>: highest SLA % among assignees.</div>
        </div>
      </Card>

      {/* Drawer */}
      <Sheet open={!!drawerUser} onOpenChange={() => setDrawerUser(null)}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="perf-drawer">
          {drawerUser && <PerformanceDrawer user={drawerUser} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────────
function PerformanceDrawer({ user }) {
  const band = performanceBand(user);
  // Dummy ticket distribution by resolution time bucket
  const buckets = [
    { label: '< 4h',   count: Math.round(user.resolved_30d * 0.30), color: 'bg-emerald-500' },
    { label: '4-24h',  count: Math.round(user.resolved_30d * 0.40), color: 'bg-emerald-400' },
    { label: '1-2d',   count: Math.round(user.resolved_30d * 0.18), color: 'bg-amber-400' },
    { label: '2-7d',   count: Math.round(user.resolved_30d * 0.09), color: 'bg-amber-500' },
    { label: '> 7d',   count: Math.round(user.resolved_30d * 0.03), color: 'bg-rose-500' },
  ];
  const maxBucket = Math.max(...buckets.map(b => b.count), 1);
  // Mock open tickets list
  const openSamples = Array.from({ length: Math.min(user.open, 5) }, (_, i) => ({
    id: `T-${user.id.slice(2)}-${100 + i}`,
    student: ['Diksha Shukla', 'Ashutosh Dubey', 'Sonal Thube', 'Yamini Reddy', 'Mamtesh Singh'][i % 5],
    rule: ['Admission Pending', 'Paid but No Active Batch', 'Refund follow-up', 'Drop-out audit', 'Fee mismatch'][i % 5],
    age_hours: Math.round(user.oldest_open_hours * (1 - i * 0.18)),
  }));

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-bold">{user.avatar}</div>
          <div>
            <p className="text-base font-semibold text-slate-800">{user.name}</p>
            <p className="text-[10px] text-slate-400 capitalize">{user.role.replace('_', ' ')}</p>
          </div>
        </SheetTitle>
      </SheetHeader>

      <div className="mt-4 space-y-4">
        {/* Overall band */}
        <Card className={`p-3 border ${band.color}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[9px] uppercase tracking-wider opacity-70 font-semibold">Performance Status</p>
              <p className="text-xl font-bold mt-0.5">{band.label}</p>
            </div>
            <Target className="w-7 h-7 opacity-30" />
          </div>
        </Card>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Open" value={user.open} sub="active tickets" tone="amber" />
          <Stat label="Resolved · 30d" value={user.resolved_30d} sub={`+${Math.round(user.resolved_30d * 0.12)} vs last`} tone="emerald" />
          <Stat label="Avg Resolution" value={fmtHours(user.avg_hours)} sub={`Median ${fmtHours(user.median_hours)}`} tone="violet" />
          <Stat label="SLA Compliance" value={`${user.sla_pct}%`} sub={`Target ≤${SLA_TARGET}h`} tone={user.sla_pct >= 80 ? 'emerald' : user.sla_pct >= 60 ? 'amber' : 'rose'} />
        </div>

        {/* Resolution time histogram */}
        <Card className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-violet-500" /> Resolution Time Distribution
          </p>
          <div className="space-y-1.5">
            {buckets.map(b => (
              <div key={b.label} className="flex items-center gap-2 text-[11px]">
                <span className="w-12 text-slate-500">{b.label}</span>
                <div className="flex-1 bg-slate-100 rounded-sm h-4 overflow-hidden">
                  <div className={`${b.color} h-full transition-all`} style={{ width: `${(b.count / maxBucket) * 100}%` }} />
                </div>
                <span className="w-7 text-right text-slate-600 font-medium">{b.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Open tickets list */}
        <Card className="p-0 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/40 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <TicketIcon className="w-3 h-3 text-amber-500" /> Open Tickets ({user.open})
            </p>
            <button className="text-[10px] text-violet-600 hover:text-violet-800 font-medium">View all →</button>
          </div>
          <div className="divide-y divide-slate-100">
            {openSamples.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No open tickets — congrats!</p>
            ) : openSamples.map(t => {
              const ageColor = t.age_hours <= 24 ? 'text-emerald-600' : t.age_hours <= 72 ? 'text-amber-600' : 'text-rose-600';
              return (
                <div key={t.id} className="px-3 py-2 hover:bg-violet-50/30 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-slate-800 truncate">{t.student}</p>
                    <span className={`text-[10px] font-semibold ${ageColor}`}>{fmtHours(t.age_hours)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-[10px] text-slate-500 truncate">{t.rule}</p>
                    <span className="text-[9px] text-slate-400">{t.id}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <p className="text-[9px] text-slate-400 italic text-center">⚡ Prototype — dummy data. Real metrics will come from /api/training/reports/tickets aggregations.</p>
      </div>
    </>
  );
}

function Stat({ label, value, sub, tone = 'slate' }) {
  const toneMap = {
    slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700' },
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700' },
  }[tone];
  return (
    <div className={`${toneMap.bg} ${toneMap.border} border rounded-md p-2.5`}>
      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${toneMap.text}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
